(() => {
  const moneyFormatter = new Intl.NumberFormat(
    document.documentElement.lang || 'en-GB',
    { style: 'currency', currency: window.Shopify?.currency?.active || 'GBP' }
  );

  const initialisePalette = (palette) => {
    if (palette.dataset.initialised === 'true') return;
    palette.dataset.initialised = 'true';

    const modal = palette.querySelector('[data-virtual-modal]');
    const openButton = palette.querySelector('[data-virtual-open]');
    const closeButtons = palette.querySelectorAll('[data-virtual-close]');
    const confirmButton = palette.querySelector('[data-virtual-confirm]');
    const choices = Array.from(palette.querySelectorAll('[data-virtual-choice]'));
    const propertyInput = palette.querySelector('[data-virtual-property-input]');
    const referenceInput = palette.querySelector('[data-virtual-reference-input]');
    const addonIdInput = palette.querySelector('[data-virtual-addon-id]');
    const addonPriceInput = palette.querySelector('[data-virtual-addon-price]');
    const selectionText = palette.querySelector('[data-virtual-selection-text]');
    const headingText = palette.querySelector('[data-virtual-selected-heading]');
    const buttonText = palette.querySelector('[data-virtual-button-label]');
    const error = palette.querySelector('[data-virtual-error]');
    const isInline = modal?.classList.contains('is-inline');

    let pendingChoice = null;

    const openModal = () => {
      if (!modal || isInline) return;

      modal.hidden = false;
      modal.classList.add('is-open');
      document.body.classList.add('scotts-virtual-modal-open');
    };

    const closeModal = () => {
      if (!modal || isInline) return;

      modal.classList.remove('is-open');
      modal.hidden = true;
      document.body.classList.remove('scotts-virtual-modal-open');
    };

    const previewChoice = (choice) => {
      pendingChoice = choice;

      choices.forEach((item) => {
        const selected = item === choice;

        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      const name = choice.dataset.choiceName || '';
      const addonPrice = Number(choice.dataset.addonPrice || 0);

      if (selectionText) {
        selectionText.textContent =
          addonPrice > 0
            ? `${name} (+${moneyFormatter.format(addonPrice / 100)})`
            : name;
      }

      if (confirmButton) {
        confirmButton.disabled = false;
      }

      if (isInline) {
        commitChoice(choice);
      }
    };

    const commitChoice = (choice) => {
      if (!choice) return;

      const name = choice.dataset.choiceName || '';
      const code = choice.dataset.choiceCode || '';
      const addonId = choice.dataset.addonVariantId || '';
      const addonPrice = choice.dataset.addonPrice || '0';

      propertyInput.value = name;
      referenceInput.value = code;
      addonIdInput.value = addonId;
      addonPriceInput.value = addonPrice;

      if (headingText) {
        headingText.textContent = name;
      }

      if (buttonText) {
        buttonText.textContent = name;
      }

      if (error) {
        error.hidden = true;
      }

      palette.dataset.selected = 'true';

      palette.dispatchEvent(
        new CustomEvent('scotts:virtual-option:change', {
          bubbles: true,
          detail: {
            propertyName: palette.dataset.propertyName,
            value: name,
            reference: code,
            addonVariantId: addonId,
            addonPrice: Number(addonPrice || 0)
          }
        })
      );

      closeModal();
    };

    openButton?.addEventListener('click', openModal);

    closeButtons.forEach((button) => {
      button.addEventListener('click', closeModal);
    });

    choices.forEach((choice) => {
      choice.addEventListener('click', () => previewChoice(choice));
    });

    confirmButton?.addEventListener('click', () => {
      commitChoice(pendingChoice);
    });

    document.addEventListener('keydown', (event) => {
      if (
        event.key === 'Escape' &&
        modal?.classList.contains('is-open')
      ) {
        closeModal();
      }
    });
  };

  const validateRequiredPalettes = (form) => {
    const section = form.closest('product-info') || document;

    const palettes = Array.from(
      section.querySelectorAll(
        '[data-scotts-virtual-option][data-required="true"]'
      )
    );

    let valid = true;

    palettes.forEach((palette) => {
      const input = palette.querySelector(
        '[data-virtual-property-input]'
      );

      const error = palette.querySelector(
        '[data-virtual-error]'
      );

      const selected = Boolean(input?.value);

      if (error) {
        error.hidden = selected;
      }

      if (!selected) {
        valid = false;

        palette.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    });

    return valid;
  };

  const addWithOptionalAddons = async (
    form,
    submitEvent
  ) => {
    const section =
      form.closest('product-info') || document;

    const addonInputs = Array.from(
      section.querySelectorAll(
        '[data-virtual-addon-id]'
      )
    );

    const addonIds = addonInputs
      .map((input) => input.value)
      .filter(Boolean);

    if (!addonIds.length) {
      return false;
    }

    submitEvent.preventDefault();
    submitEvent.stopImmediatePropagation();

    if (!validateRequiredPalettes(form)) {
      return true;
    }

    const variantId =
      form.querySelector('[name="id"]')?.value;

    const quantity = Number(
      form.querySelector('[name="quantity"]')?.value || 1
    );

    const propertyInputs = Array.from(
      section.querySelectorAll(
        '[data-virtual-property-input], [data-virtual-reference-input]'
      )
    );

    const properties = {};

    propertyInputs.forEach((input) => {
      const match = input.name.match(
        /^properties\[(.*)\]$/
      );

      if (match && input.value) {
        properties[match[1]] = input.value;
      }
    });

    const items = [
      {
        id: Number(variantId),
        quantity,
        properties
      }
    ];

    addonIds.forEach((id) => {
      items.push({
        id: Number(id),
        quantity,
        properties: {
          '_Added with': document.title,
          '_Virtual option add-on': 'true'
        }
      });
    });

    const submitButton =
      form.querySelector('[type="submit"]');

    submitButton?.setAttribute(
      'disabled',
      'disabled'
    );

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        }
      );

      if (!response.ok) {
        throw new Error(
          'Unable to add configured product.'
        );
      }

      window.location.href =
        `${window.Shopify.routes.root}cart`;
    } catch (error) {
      console.error(error);

      submitButton?.removeAttribute('disabled');

      alert(
        'There was a problem adding this configuration. Please try again.'
      );
    }

    return true;
  };

  const initialiseForms = () => {
    document
      .querySelectorAll(
        'product-form form[data-type="add-to-cart-form"]'
      )
      .forEach((form) => {
        if (
          form.dataset.virtualOptionsReady === 'true'
        ) {
          return;
        }

        form.dataset.virtualOptionsReady = 'true';

        form.addEventListener(
          'submit',
          async (event) => {
            if (!validateRequiredPalettes(form)) {
              event.preventDefault();
              event.stopImmediatePropagation();
              return;
            }

            await addWithOptionalAddons(
              form,
              event
            );
          },
          true
        );
      });
  };

  const initialiseAll = (root = document) => {
    root
      .querySelectorAll(
        '[data-scotts-virtual-option]'
      )
      .forEach(initialisePalette);

    initialiseForms();
  };

  document.addEventListener(
    'DOMContentLoaded',
    () => initialiseAll()
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => initialiseAll(event.target)
  );
})();